import { useEffect } from 'react';

/**
 * Keeps the screen on while `active`. The browser drops the lock whenever the page is
 * hidden, so it is requested again on each return to the foreground.
 */
export function useWakeLock(active: boolean): void {
  useEffect(() => {
    if (!active || !('wakeLock' in navigator)) return;

    let lock: WakeLockSentinel | undefined;
    let requesting = false;
    let stopped = false;

    const acquire = () => {
      if (stopped || lock || requesting || document.visibilityState !== 'visible') return;
      requesting = true;
      navigator.wakeLock.request('screen').then(
        (sentinel) => {
          requesting = false;
          if (stopped) {
            void sentinel.release();
            return;
          }
          lock = sentinel;
          sentinel.addEventListener('release', () => {
            if (lock === sentinel) lock = undefined;
          });
        },
        (err: unknown) => {
          requesting = false;
          console.warn('Screen wake lock was not acquired', err);
        },
      );
    };

    acquire();
    document.addEventListener('visibilitychange', acquire);
    return () => {
      stopped = true;
      document.removeEventListener('visibilitychange', acquire);
      void lock?.release();
    };
  }, [active]);
}
